import type { AfterViewInit, OnChanges, OnInit, SimpleChange, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Input,
  input,
  output,
  signal,
  untracked,
  viewChild,
  ViewContainerRef,
} from '@angular/core';

import type { Menu, MenuItemType, ThemeType } from '@evc/web-components';
import {
  AvailableThemes,
  Maybe,
  ModalService,
  ResponsiveService,
  ThemeService,
  ToastComponent,
  ToastService,
  VERSION as WEB_COMPONENTS_VERSION,
  WebComponentsConfigService,
} from '@evc/web-components';

import { AuthService } from './clients/core/auth/service/auth.service';
import { OrganizationsService } from './clients/core/organizations/organizations.service';
import type { Organization } from './clients/core/organizations/organizations.type';
import { UserService } from './clients/core/user/user.service';
import type { UserApi, UserApiPossibilities } from './clients/core/user/user.type';
import { AuthLicenseCapacityModalComponent } from './components/auth-license-capacity-modal/auth-license-capacity-modal';
import { AuthOrganizationSelectionModalComponent } from './components/auth-organization-selection-modal/auth-organization-selection-modal';
import { LoaderComponent } from './components/loader/loader.component';
import { PlatformConfigService } from './features/config/config.service';
import { ApplicationURIs } from './features/config/config.type';
import type { AbstractI18nService } from './features/i18n/i18n-service.type';
import type { NavigationsMode } from './features/navigation';
import { NavigationsComponent } from './features/navigation';
import { FakeLeftbarComponent } from './features/navigation/fake-leftbar/fake-leftbar';
import { SearchService } from './features/search/search.service';
import { SearchFn } from './features/search/search.type';
import { Guid } from './features/types/api-objects.type';
import { AppIdentity } from './features/types/app-identity.type';
import type { ApplicationType } from './features/types/user-menu.type';
import { PlatformService } from './platform.service';
import { VERSION as PLATFORM_VERSION } from './version';

@Component({
  selector: 'evc-platform',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FakeLeftbarComponent,
    NavigationsComponent,
    LoaderComponent,
    AuthOrganizationSelectionModalComponent,
    AuthLicenseCapacityModalComponent,
    ToastComponent,
  ],
  templateUrl: './platform.component.html',
  styleUrls: ['./platform.component.scss'],
  host: {
    '[attr.data-resizing]': 'isResizing()',
    '[attr.data-platform-version]': 'PLATFORM_VERSION',
    '[attr.data-web-components-version]': 'WEB_COMPONENTS_VERSION',
  },
})
export class PlatformComponent implements OnChanges, OnInit, AfterViewInit {
  readonly #platformService = inject(PlatformService);
  readonly #platformConfigService = inject(PlatformConfigService);
  readonly #webComponentConfigService = inject(WebComponentsConfigService);
  readonly #themeService = inject(ThemeService);
  readonly #searchService = inject(SearchService);
  readonly #authService = inject(AuthService);
  readonly #userService = inject(UserService);
  readonly #organizationsService = inject(OrganizationsService);
  readonly #destroyRef = inject(DestroyRef);
  readonly toastService = inject(ToastService);
  readonly #modalService = inject(ModalService);
  readonly #responsiveService = inject(ResponsiveService);

  readonly ready = output();

  readonly inputAppIdentification = input<AppIdentity>();
  readonly inputLinks = input<ApplicationURIs>();
  readonly inputHelpMenu = input<MenuItemType[]>();
  readonly inputSettingsMenu = input<MenuItemType[]>();
  readonly inputTopTabs = input<MenuItemType[]>();
  readonly inputLeftBar = input<Menu[]>();
  readonly inputForcedLayout = input<Maybe<NavigationsMode>>(undefined);
  readonly isStandaloneApplication = input(false);
  readonly displayUserMenu = input(true);
  readonly inputForcedTheme = input<Exclude<ThemeType, 'auto'>>();
  readonly isIms = computed(() => this.inputForcedTheme() === 'ims');
  readonly inputSearch = input<SearchFn>();
  readonly basePath = input(this.#webComponentConfigService.get('ASSETS_BASE_PATH'));
  readonly autoInitService = input(true);

  readonly login = input<Maybe<() => void>>(undefined);
  readonly logout = input<Maybe<() => void>>(undefined);
  readonly changeOrganization = input<Maybe<(organizationId: Guid) => void>>(undefined);

  readonly PLATFORM_VERSION = PLATFORM_VERSION;
  readonly WEB_COMPONENTS_VERSION = WEB_COMPONENTS_VERSION;
  protected readonly isLeftbarOpen = signal(true);

  modalContainer = viewChild('modalContainer', { read: ViewContainerRef });

  /** we cannot import shared so need to provide via input */
  readonly i18nAppService = input<Maybe<AbstractI18nService>>();

  @Input() set inputUserProfile(user: UserApi | undefined) {
    if (user) {
      if (this.#platformConfigService.useConnect()) {
        throw new Error('inputUserProfile is only available in legacy mode');
      }

      const { applications } = user as UserApiPossibilities;
      if (applications) {
        this.inputUserApplications = applications;
      }
      const { organizations } = user as UserApiPossibilities;
      if (organizations) {
        this.inputUserOrganizations = organizations as Organization[];
      }
    }

    this.#userService.setUser(user);
  }

  @Input() public set inputUserOrganizations(organizations: Organization[]) {
    if (!organizations?.length) {
      return;
    }
    this.#organizationsService.setEntries(organizations);
  }

  @Input() set inputUserApplications(apps: ApplicationType[]) {
    this.#platformConfigService.set('applications', apps);
  }

  readonly navigationSecondary = this.#platformConfigService.getMenu('leftbar');

  readonly isResizing = this.#responsiveService.isResizing.bind(this.#responsiveService);

  protected readonly showAuthOrganizationSelectionModal =
    this.#authService.shouldPromptOrganizationSelection;
  protected readonly showAuthLicenseCapacityModal = this.#authService.shouldPromptLicenseCapacity;

  #syncStandaloneApplication = effect(() =>
    this.#platformConfigService.set('isStandaloneApplication', this.isStandaloneApplication()),
  );

  #emitOnReady = effect(() => {
    if (!this.#platformService.ready()) return;
    untracked(() => {
      this.ready.emit();
      this.#emitOnReady.destroy();
    });
  });

  async ngOnInit(): Promise<void> {
    if (this.autoInitService()) {
      this.#platformService.init(this.i18nAppService());
    }

    this.#destroyRef.onDestroy(() => {
      this.#emitOnReady.destroy();
    });
  }

  ngAfterViewInit(): void {
    this.#modalService.registerViewContainerRef(this.modalContainer()!);
  }

  protected onOrgSelected(orgId: Guid): void {
    this.#platformService.confirmOrganization(orgId);
  }

  protected onUseAnotherAccount(): void {
    this.#authService.login({
      prompt: 'select_account',
      state: JSON.stringify({ promptOrgSelection: true }),
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    const changed = (change: SimpleChange | undefined, key = 'currentValue') => {
      if (!change) return false;

      return Object.prototype.hasOwnProperty.call(change, key);
    };

    if (changed(changes.inputForcedTheme)) {
      const inputForcedTheme: Exclude<ThemeType, 'auto'> | undefined =
        changes.inputForcedTheme.currentValue;
      this.#setForcedTheme(inputForcedTheme);
    }

    if (changed(changes.inputSearch)) {
      this.#searchService.setSearch(changes.inputSearch.currentValue);
    }

    if (changed(changes.inputAppIdentification)) {
      const inputIdentification: AppIdentity = changes.inputAppIdentification.currentValue;
      this.#platformConfigService.merge('app', inputIdentification);
    }

    if (changed(changes.inputLinks)) {
      const uris: ApplicationURIs = changes.inputLinks.currentValue;
      this.#platformConfigService.merge('uris', uris);
    }

    if (changed(changes.inputHelpMenu)) {
      const items: MenuItemType[] = changes.inputHelpMenu.currentValue;
      this.#platformConfigService.updateMenu('help', items);
    }

    if (changed(changes.inputSettingsMenu)) {
      const items: MenuItemType[] = changes.inputSettingsMenu.currentValue;
      this.#platformConfigService.updateMenu('settings', items);
    }

    if (changed(changes.inputTopTabs)) {
      const inputTopTabs: MenuItemType[] = changes.inputTopTabs.currentValue;
      this.#platformConfigService.merge('topTabs', inputTopTabs);
    }

    if (changed(changes.inputLeftBar)) {
      const inputLeftBar: Menu[] = changes.inputLeftBar.currentValue;
      this.#platformConfigService.merge('menus', {
        leftbar: inputLeftBar,
      });
    }

    if (changed(changes.basePath)) {
      const basePath: string = changes.basePath.currentValue;
      this.#webComponentConfigService.set('ASSETS_BASE_PATH', basePath);
    }
  }

  #setForcedTheme(inputForcedTheme: Exclude<ThemeType, 'auto'> | undefined): void {
    if (!inputForcedTheme) return;

    if (!AvailableThemes.includes(inputForcedTheme)) {
      throw new Error(
        `[platform] Given theme ${inputForcedTheme} must be one of ${AvailableThemes.join(',')}`,
      );
    }

    this.#platformConfigService.set('theme', inputForcedTheme);
    this.#themeService.setTheme(inputForcedTheme);
  }
}
